import React, { useState, useEffect } from "react";
import { Card, CardBody, Button, Container, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { templateColumns } from "../helper/templateconstant";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { perPageData, showToastMessage } from "common/service/CommonService";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { useTemplateApi } from "modules/Library/Template/api/useTemplateService";
import { queryClient } from "api/queryClient";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import Template from "../helper/template.types";

const TemplateList: React.FC = () => {
  document.title = "Template | ZENTIXS VISTA";

  // Navigation
  const navigate = useNavigate();

  //-------------------------------------------------------------------------------------------
  //--> Use STATE
  //-------------------------------------------------------------------------------------------
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<Template | null>(null);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  //-------------------------------------------------------------------------------------------
  //--> API HOOKS
  //-------------------------------------------------------------------------------------------
  const { SelectAllQuery, DeleteMutation } = useTemplateApi({
    searchText,
    pageNumber: currentPageNumber,
    pageSize: perPageData,
  });

  const templateData = SelectAllQuery.data?.data || [];
  const totalRecords = templateData[0]?.total_count || 0;
  const isLoading = SelectAllQuery.isLoading || SelectAllQuery.isFetching;

  //-------------------------------------------------------------------------------------------
  //--> Toggles
  //-------------------------------------------------------------------------------------------
  const toggleDeleteReason = (): void => setShowDeleteReason(!showDeleteReason);

  //-------------------------------------------------------------------------------------------
  //--> ON SEARCH
  //-------------------------------------------------------------------------------------------
  const onSearch = (text: string | null) => {
    const next = (text ?? "").trim();
    if (next === searchText && currentPageNumber === 1) return;
    setCurrentPageNumber(1);
    setSearchText(next);
  };

  //-------------------------------------------------------------------------------------------
  //--> On Edit
  //-------------------------------------------------------------------------------------------
  const onEdit = (item: Template) => {
    navigate(`/template/edit/${item.templateid}`, { state: item });
  };

  //-------------------------------------------------------------------------------------------
  //--> On View
  //-------------------------------------------------------------------------------------------
  const onView = (item: Template): void => {
    navigate(`/template/view/${item.templateid}`, { state: item });
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Delete
  //------------------------------------------------------------------------------------------
  const onDeleteClick = (item: Template): void => {
    setSelectedRecord(item);
    setDeleteModal(true);
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason?: string;
  }): void => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      {
        templateid: selectedRecord.templateid,
        updatereason: reasonPayload.reason,
        // Add other required fields for delete if necessary
      },
      {
        onSuccess: (res: any) => {
          const totalRecords = templateData[0]?.total_count || 0;
          const lastPage = Math.ceil((totalRecords - 1) / perPageData);
          setCurrentPageNumber(
          Math.max(1, Math.min(currentPageNumber, lastPage)),
          );
          showToastMessage("success", res?.message || "Template deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["template-selectAll"] });
          setShowDeleteReason(false);
          setDeleteModal(false);
          setSelectedRecord(null);
          setSearchText("");
        },
        onError: (error: any) => {
          showToastMessage(
            "error",
            (error?.message ?? error) || "Failed to delete template",
          );
        },
      },
    );
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Template",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Template_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", (error?.message ?? error) || "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className="table_wrapper">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.TEMPLATE}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  outline
                  size="sm"
                  module={PERMISSION_MODULES.TEMPLATE}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => navigate("/template/add")}
                >
                  <i className="ri-add-line"></i> Add
                </CommonButton>
              </div>
            </div>

            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.TEMPLATE}
              columns={templateColumns}
              data={templateData}
              actions={{
                onView: onView,
                onDelete: onDeleteClick,
                onEdit: onEdit,
              }}
              loading={isLoading}
              pageSize={perPageData}
              currentPage={currentPageNumber}
              onPageChange={setCurrentPageNumber}
              totalRecords={totalRecords}
            />
          </CardBody>
        </Card>

        {/* Delete Template Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={() => setDeleteModal(false)}
            recordId={selectedRecord?.templateid ?? 0}
            recordName={
              selectedRecord?.name || selectedRecord?.templatename || ""
            }
            onDeleteClick={(payload: any) => confirmDelete(payload)}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={toggleDeleteReason}
          />
        )}
      </Container>
      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default TemplateList;
